import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/api";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const DEFAULT_COVER = "https://images.pexels.com/photos/5423331/pexels-photo-5423331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900";

export default function Account() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [coverUrl, setCoverUrl] = useState(DEFAULT_COVER);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings/auth-cover").then((response) => {
      if (response.data?.url) setCoverUrl(response.data.url);
    }).catch(() => {});
  }, []);

  async function submit(event) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="account-page" className="spotlight flex min-h-screen items-center bg-onyx px-4 pt-24">
      <Seo title="Account" description="Sign in or create a mellowjmedia customer account to access purchased digital downloads." />
      <div className="mx-auto grid w-full max-w-5xl overflow-hidden border border-gold/20 bg-carbon lg:grid-cols-2">
        <div data-testid="auth-cover-panel" className="hidden bg-cover bg-center p-10 lg:block" style={{ backgroundImage: `linear-gradient(rgba(7,7,8,0.45),rgba(7,7,8,0.88)), url('${coverUrl}')` }}>
          <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">Collector access</p>
          <h1 className="mt-6 font-display text-5xl font-light leading-tight text-white">Keep every original within reach.</h1>
        </div>
        <form data-testid="auth-form" onSubmit={submit} className="p-6 sm:p-10">
          <div className="mb-8 grid grid-cols-2 gap-px bg-white/10 p-px">
            <button data-testid="login-mode-button" type="button" onClick={() => setMode("login")} className={`py-3 font-mono text-xs uppercase tracking-[0.22em] ${mode === "login" ? "bg-gold text-onyx" : "bg-carbon text-zinc-400"}`}>Login</button>
            <button data-testid="register-mode-button" type="button" onClick={() => setMode("register")} className={`py-3 font-mono text-xs uppercase tracking-[0.22em] ${mode === "register" ? "bg-gold text-onyx" : "bg-carbon text-zinc-400"}`}>Register</button>
          </div>
          <h2 className="font-display text-4xl font-light text-white">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="mt-3 text-sm leading-7 text-zinc-500">Accounts are optional for checkout and keep your paid downloads organized.</p>
          <div className="mt-8 space-y-4">
            {mode === "register" && <Input data-testid="register-name-input" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Full name" className="h-12 border-white/10 bg-white/[0.03] text-white" />}
            <Input data-testid="auth-email-input" required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Email address" className="h-12 border-white/10 bg-white/[0.03] text-white" />
            <Input data-testid="auth-password-input" required minLength={8} type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="Password" className="h-12 border-white/10 bg-white/[0.03] text-white" />
          </div>
          {error && <p data-testid="auth-error-message" className="mt-4 border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}
          <Button data-testid="auth-submit-button" disabled={loading} className="mt-7 h-13 w-full rounded-none bg-gold py-6 font-mono text-xs uppercase tracking-[0.24em] text-onyx hover:bg-titanium">
            {loading ? "Working" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
